import React, { useEffect, useRef, useState, useCallback } from 'react';
import { ChevronDown, ChevronLeft, ChevronRight } from 'lucide-react';
import { heroSlides } from '../../data/mock';

const HeroSection = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const heroRef = useRef(null);
  const progressInterval = useRef(null);
  const SLIDE_DURATION = 5000;

  // Progress bar animation
  useEffect(() => {
    if (isPaused) return;
    
    setProgress(0);
    const startTime = Date.now();
    
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / SLIDE_DURATION) * 100, 100);
      setProgress(newProgress);
      
      if (newProgress >= 100) {
        clearInterval(progressInterval.current);
        setCurrentSlide((prev) => (prev + 1) % heroSlides.length);
      }
    }, 30);

    return () => clearInterval(progressInterval.current);
  }, [currentSlide, isPaused]);

  const goToSlide = useCallback((index) => {
    clearInterval(progressInterval.current);
    setCurrentSlide(index);
    setProgress(0);
  }, []);

  const nextSlide = useCallback(() => {
    goToSlide((currentSlide + 1) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  const prevSlide = useCallback(() => {
    goToSlide((currentSlide - 1 + heroSlides.length) % heroSlides.length);
  }, [currentSlide, goToSlide]);

  // Touch handlers
  const touchStartX = useRef(0);
  const touchEndX = useRef(0);

  const handleTouchStart = (e) => {
    touchStartX.current = e.touches[0].clientX;
    setIsPaused(true);
  };

  const handleTouchMove = (e) => {
    touchEndX.current = e.touches[0].clientX;
  };

  const handleTouchEnd = () => {
    const diff = touchStartX.current - touchEndX.current;
    if (Math.abs(diff) > 50) {
      if (diff > 0) nextSlide();
      else prevSlide();
    }
    setIsPaused(false);
  };

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <section 
      id="inicio" 
      ref={heroRef} 
      className="relative h-screen overflow-hidden"
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      data-testid="hero-section"
    >
      {/* Background Images */}
      {heroSlides.map((slide, index) => (
        <div
          key={slide.id}
          className={`absolute inset-0 transition-all duration-700 ease-out ${
            index === currentSlide 
              ? 'opacity-100 scale-100' 
              : 'opacity-0 scale-105'
          }`}
        >
          <img
            src={slide.image}
            alt={slide.title}
            className="w-full h-full object-cover"
          />
        </div>
      ))}

      {/* Overlay */}
      <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/70" />

      {/* Content */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 text-center">
          {/* Main Title with animation */}
          <div className="mb-8">
            <h1 
              key={`title-${currentSlide}`}
              className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-semibold text-white mb-4 tracking-tight animate-fade-in-up"
            >
              {heroSlides[currentSlide].title}
            </h1>
            <p 
              key={`subtitle-${currentSlide}`}
              className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-medium text-white/70 animate-fade-in-up-delay"
            >
              {heroSlides[currentSlide].subtitle}
            </p>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-10 sm:mt-12">
            <button
              onClick={() => scrollToSection('nosotros')}
              className="group w-full sm:w-auto px-8 py-4 bg-white text-black font-medium rounded-full transition-all duration-300 hover:bg-white/90 flex items-center justify-center gap-3"
              data-testid="hero-discover-btn"
            >
              <span>Descubre más</span>
              <ChevronDown size={20} className="group-hover:translate-y-1 transition-transform" />
            </button>
          </div>

          {/* Partner Logos - Mobile (Inside content, between CTA and Desliza) */}
          <div className="flex md:hidden items-center justify-center gap-3 mt-14 opacity-70">
            <a href="https://www.fifa.com/football-agents" target="_blank" rel="noopener noreferrer" className="relative group flex-shrink-0 hover:opacity-100 transition-opacity">
              <img src={`${process.env.PUBLIC_URL}/images/partners/fifa_agent.png`} alt="FIFA Agent" className="h-4 w-auto" />
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
                Agente FIFA
              </span>
            </a>
            <div className="w-px h-3 bg-white/40 flex-shrink-0" />
            <a href="https://www.nike.com" target="_blank" rel="noopener noreferrer" className="relative group flex-shrink-0 hover:opacity-100 transition-opacity">
              <img src={`${process.env.PUBLIC_URL}/images/partners/nike.png`} alt="Nike" className="h-3 w-auto" />
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
                Convenio Nike
              </span>
            </a>
            <div className="w-px h-3 bg-white/40 flex-shrink-0" />
            <a href="https://sportcore.artificialmente.uy" target="_blank" rel="noopener noreferrer" className="relative group flex-shrink-0 hover:opacity-100 transition-opacity">
              <img src={`${process.env.PUBLIC_URL}/images/partners/sportcore.png`} alt="SportCore" className="h-4 w-auto" />
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
                SportCore
              </span>
            </a>
            <div className="w-px h-3 bg-white/40 flex-shrink-0" />
            <a href="https://artificialmente.uy" target="_blank" rel="noopener noreferrer" className="relative group flex-shrink-0 hover:opacity-100 transition-opacity">
              <img src={`${process.env.PUBLIC_URL}/images/partners/artificialmente.png`} alt="Artificialmente" className="h-3 w-auto" />
              <span className="absolute -top-8 left-1/2 -translate-x-1/2 px-2 py-1 bg-white text-black text-[10px] font-medium rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
                Artificialmente
              </span>
            </a>
          </div>
        </div>
      </div>

      {/* Navigation Arrows - Desktop */}
      <button
        onClick={prevSlide}
        className="hidden md:flex absolute left-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full items-center justify-center text-white transition-all duration-300 border border-white/20 group"
        data-testid="hero-prev-btn"
      >
        <ChevronLeft size={28} className="group-hover:-translate-x-0.5 transition-transform" />
      </button>
      <button
        onClick={nextSlide}
        className="hidden md:flex absolute right-6 top-1/2 -translate-y-1/2 w-14 h-14 bg-white/10 hover:bg-white/20 backdrop-blur-sm rounded-full items-center justify-center text-white transition-all duration-300 border border-white/20 group"
        data-testid="hero-next-btn"
      >
        <ChevronRight size={28} className="group-hover:translate-x-0.5 transition-transform" />
      </button>

      {/* Mobile Swipe Indicator */}
      <div className="absolute bottom-44 left-1/2 -translate-x-1/2 flex items-center gap-3 md:hidden">
        <div className="flex items-center gap-2 text-white/40 text-sm">
          <ChevronLeft size={16} className="animate-pulse-left" />
          <span>Desliza</span>
          <ChevronRight size={16} className="animate-pulse-right" />
        </div>
      </div>

      {/* Progress Indicators */}
      <div className="absolute bottom-32 sm:bottom-24 left-1/2 -translate-x-1/2 flex items-center gap-3">
        {heroSlides.map((_, index) => (
          <button
            key={index}
            onClick={() => goToSlide(index)}
            className="relative h-1 rounded-full overflow-hidden transition-all duration-300 bg-white/20"
            style={{ width: index === currentSlide ? '48px' : '12px' }}
            data-testid={`hero-indicator-${index}`}
          >
            {index === currentSlide && (
              <div 
                className="absolute inset-y-0 left-0 bg-white rounded-full transition-all"
                style={{ width: `${progress}%` }}
              />
            )}
          </button>
        ))}
      </div>

      {/* Slide Counter - Mobile */}
      <div className="absolute bottom-20 right-4 text-white/50 text-sm font-light md:hidden">
        {String(currentSlide + 1).padStart(2, '0')} / {String(heroSlides.length).padStart(2, '0')}
      </div>

      {/* Partner Logos - Desktop (Centered) */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 hidden md:flex items-center gap-4">
        <a href="https://www.fifa.com/football-agents" target="_blank" rel="noopener noreferrer" className="relative group opacity-50 hover:opacity-90 transition-opacity duration-300">
          <img src={`${process.env.PUBLIC_URL}/images/partners/fifa_agent.png`} alt="FIFA Agent" className="h-6 w-auto" />
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
            Agente FIFA Certificado
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
          </span>
        </a>
        <div className="w-px h-5 bg-white/30" />
        <a href="https://www.nike.com" target="_blank" rel="noopener noreferrer" className="relative group opacity-50 hover:opacity-90 transition-opacity duration-300">
          <img src={`${process.env.PUBLIC_URL}/images/partners/nike.png`} alt="Nike" className="h-5 w-auto" />
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
            Convenio Nike
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
          </span>
        </a>
        <div className="w-px h-5 bg-white/30" />
        <a href="https://sportcore.artificialmente.uy" target="_blank" rel="noopener noreferrer" className="relative group opacity-50 hover:opacity-90 transition-opacity duration-300">
          <img src={`${process.env.PUBLIC_URL}/images/partners/sportcore.png`} alt="SportCore" className="h-6 w-auto" />
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
            Plataforma profesional de gestión de jugadores
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
          </span>
        </a>
        <div className="w-px h-5 bg-white/30" />
        <a href="https://artificialmente.uy" target="_blank" rel="noopener noreferrer" className="relative group opacity-50 hover:opacity-90 transition-opacity duration-300">
          <img src={`${process.env.PUBLIC_URL}/images/partners/artificialmente.png`} alt="Artificialmente" className="h-5 w-auto" />
          <span className="absolute -top-10 left-1/2 -translate-x-1/2 px-3 py-1.5 bg-white text-black text-xs font-medium rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none shadow-lg">
            Desarrollo Artificialmente - Inteligencia Artificial
            <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 w-2 h-2 bg-white rotate-45" />
          </span>
        </a>
      </div>

      {/* Scroll Indicator - Desktop only, slightly above center */}
      <div className="absolute bottom-16 left-1/2 -translate-x-1/2 animate-bounce hidden md:block">
        <ChevronDown size={28} className="text-white/40" />
      </div>
    </section>
  );
};

export default HeroSection;
